import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import LandingScreen from './components/LandingScreen';
import { Character } from './game/characters';
import { SoundManager } from './game/SoundManager';
import AssetPreloader from './components/AssetPreloader';
import { loadSavedControlPreset, presetControlMappings, PresetMapping } from './components/controlMapping';
import { track } from '@vercel/analytics';
import NetworkManager from './multiplayer/services/NetworkManager';
import { useTimerRegistry } from './hooks/useTimerRegistry';
import { debugLog } from './utils/debug';

const MemeSelect = lazy(() => import('./components/MemeSelect'));
const GameScene = lazy(() => import('./components/GameScene'));
const InterstitialScreen = lazy(() => import('./components/InterstitialScreen'));
const AIGameMode = lazy(() => import('./components/AIGameMode'));
const TestMemeGenerator = lazy(() => import('./components/TestMemeGenerator'));
const MultiplayerLobby = lazy(() => import('./multiplayer/components/MultiplayerLobby'));

// Add this at the top of App.tsx after your imports:
declare global {
  interface Window {
    environmentSync?: {
      floorIndex: number;
      backgroundIndex: number;
      skyColor: string;
    };
  }
}
// Game state types - add MEME_TEST state
// In App.tsx, update your GameState type definition
type GameState = 'LANDING' | 'CHARACTER_SELECT' | 'INTERSTITIAL' | 'PLAYING' | 'TESTING' | 'ANIMATION_TEST' | 'AI_MODE' | 'MEME_TEST' | 'MULTIPLAYER';
declare global {
  interface Window {
    dankmultiplayer?: {
      setGameState: (state: GameState) => void;
      getGameState: () => GameState;
      forceGameStart: () => void;
    };
  }
}
const App: React.FC = () => {
  const timers = useTimerRegistry();
  // Game state
  const [gameState, setGameState] = useState<GameState>('LANDING');
  const [musicOn, setMusicOn] = useState<boolean>(false);
  
  // Selected characters
  const [player1Character, setPlayer1Character] = useState<Character | null>(null);
  const [player2Character, setPlayer2Character] = useState<Character | null>(null);
  
  // Computer opponent state
  const [isComputerOpponent, setIsComputerOpponent] = useState(true);

  const [initialPreloadComplete, setInitialPreloadComplete] = useState<boolean>(false);
  const [preloadProgress, setPreloadProgress] = useState<number>(0);

  // In App.tsx, add this with your other state variables
const [useModernChallengeMode, setUseModernChallengeMode] = useState<boolean>(true);
  // State for animated models toggle
// State for animated models toggle
const [useAnimatedModels, setUseAnimatedModels] = useState<boolean>(true);

const [isFromPortal, setIsFromPortal] = useState<boolean>(false);
const [portalRefUrl, setPortalRefUrl] = useState<string>('');


// Initialize with saved preset
const [currentControlPreset, setCurrentControlPreset] = useState(loadSavedControlPreset());
const [controlMapping, setControlMapping] = useState(presetControlMappings[loadSavedControlPreset()]);
const [multiplayerRoomId, setMultiplayerRoomId] = useState<string | null>(null);
const [isMultiplayerHost, setIsMultiplayerHost] = useState<boolean>(false);


  const [multiplayer, setMultiplayer] = useState({
  roomId: null as string | null,
  isHost: false,
  connected: false,
  opponentReady: false,
  playerReady: false
});

const lazyScreenFallback = (
  <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', background: '#111', color: '#fff' }}>
    Loading...
  </div>
);

useEffect(() => {
  SoundManager.preloadSounds();
}, []);


useEffect(() => {
  // Expose setGameState for debugging
  if (typeof window !== 'undefined') {
    window.dankmultiplayer = {
      setGameState: (state: GameState) => {
        debugLog(`Debug: Setting game state to ${state}`);
        setGameState(state);
      },
      getGameState: () => gameState,
      forceGameStart: () => {
        const networkManager = NetworkManager.getInstance();
        if (networkManager) {
          debugLog("Forcing game start from global helper");
          networkManager.forceGameStart();
          timers.setTimeout(() => {
            setGameState('INTERSTITIAL');
          }, 500);
        }
      }
    };
  }
  
  return () => {
    // Clean up
    if (typeof window !== 'undefined' && window.dankmultiplayer) {
      delete window.dankmultiplayer;
    }
  };
}, []);
// In App.tsx, simplify the game start logic to have just ONE effect handling game start
// Replace ALL your existing multiplayer game start effects with this single one:

// This is the ONLY effect you should have for game start synchronization
// Fix for App.tsx - Enhance character synchronization
// Replace the useEffect for game start synchronization in App.tsx

// Fix for App.tsx - Enhance character synchronization
// Replace the useEffect for game start synchronization in App.tsx
// Update the game start useEffect in App.tsx to ensure character data is properly synced

// In App.tsx, replace both useEffects with this single one
// Replace your current useEffect for game start with this one
// Keep only this useEffect in App.tsx for game start logic:

useEffect(() => {
  // Skip if not in multiplayer
  if (!multiplayer.roomId) return;
  
  const networkManager = NetworkManager.getInstance();
  let startGameTimer: ReturnType<typeof setTimeout> | null = null;
  let transitionTimer: ReturnType<typeof setTimeout> | null = null;
  
  // Check if conditions are met for game start
  const bothPlayersReady = multiplayer.playerReady && multiplayer.opponentReady;
  
  debugLog(`Ready state: Local=${multiplayer.playerReady}, Opponent=${multiplayer.opponentReady}`);
  
  // ONLY proceed if both players are ready
  if (bothPlayersReady && multiplayer.isHost) {
    debugLog("HOST: Both players ready - starting game");
    setIsComputerOpponent(false);
    networkManager.startGameWithCharacters(player1Character, player2Character);
  }

  const charactersSelected = player1Character && player2Character;
  const readyToStart = bothPlayersReady && charactersSelected;
  
  debugLog(`Game start conditions: Ready=${bothPlayersReady}, Characters=${charactersSelected}`, {
    player1: player1Character?.id || 'null',
    player2: player2Character?.id || 'null',
    useAnimatedModels: useAnimatedModels,
    playerReady: multiplayer.playerReady,
    opponentReady: multiplayer.opponentReady
  });
  
  // If host and conditions met, send start signal
  if (readyToStart && multiplayer.isHost) {
    debugLog("HOST: All conditions met for game start - initiating with characters!");
    
    // CRITICAL: Force isComputerOpponent to false for multiplayer mode
    setIsComputerOpponent(false);
    
    // Use timeout to ensure UI has updated
    startGameTimer = timers.setTimeout(() => {
      // Send start game with character data explicitly included
      networkManager.startGameWithCharacters(player1Character, player2Character);
    }, 300);
  }
  
    // Create a handler for game start event - BOTH host and guest use this
const handleGameStart = (data: any) => {
  debugLog("🎮🎮🎮 GAME START EVENT HANDLER EXECUTING!", data);
  
  // CRITICAL: Force isComputerOpponent to false for multiplayer
  setIsComputerOpponent(false);
  
  // Extract and store environment settings
  if (data.environment) {
    debugLog("Found environment data in game start event", data.environment);
    
    if (typeof window !== 'undefined') {
      // Store COMPLETE environment data
      window.environmentSync = data.environment;
    }
  }
  
  // Extract character data if available
  if (data.characters) {
    debugLog("Found character data in game start event", {
      player1Id: data.characters.player1?.id,
      player2Id: data.characters.player2?.id
    });
    
    try {
      // If character data is included in the event, use it
      if (data.characters.player1) {
        setPlayer1Character(data.characters.player1);
      }
      
      if (data.characters.player2) {
        setPlayer2Character(data.characters.player2);
      }
    } catch (err) {
      console.error("Error processing character data:", err);
    }
  }
  
  // Small delay to ensure character data is set before transitioning
  transitionTimer = timers.setTimeout(() => {
    debugLog("👉 CHANGING GAME STATE TO INTERSTITIAL with multiplayer=true");
    setGameState('INTERSTITIAL');
  }, 300);
};
  
  // Always register the game start handler, regardless of ready state
  debugLog("Registering gameStart handler for multiplayer sync");
  networkManager.offAll('gameStart'); // Remove all existing handlers
  networkManager.on('gameStart', handleGameStart);
  
  // Clean up when unmounting
  return () => {
    timers.clearTimeout(startGameTimer);
    timers.clearTimeout(transitionTimer);
    debugLog("Cleaning up gameStart handler");
    networkManager.off('gameStart', handleGameStart);
  };
}, [
  multiplayer.roomId,
  multiplayer.isHost,
  multiplayer.playerReady,
  multiplayer.opponentReady,
  player1Character,
  player2Character,
  timers
]);

const [multiplayerPlayerNames, setMultiplayerPlayerNames] = useState({
  local: "",
  remote: ""
});

// In App.tsx - when starting multiplayer game
const handleStartMultiplayerGame = (roomId: string, isHost: boolean, playerName: string = "You", opponentName: string = "Opponent") => {
  debugLog(`Starting multiplayer game in room ${roomId} as ${isHost ? 'host' : 'guest'} with names ${playerName}/${opponentName}`);
  
    // CRITICAL: Set computer opponent to false for multiplayer
    setIsComputerOpponent(false);
  // Store multiplayer information
  setMultiplayerRoomId(roomId);
  setIsMultiplayerHost(isHost);
  setMultiplayer({
    ...multiplayer,
    roomId: roomId,
    isHost: isHost,
    connected: true
  });
  
  // Critical: Store player names correctly
  setMultiplayerPlayerNames({
    local: playerName || "You",
    remote: opponentName || "Opponent"
  });
  
  setGameState('CHARACTER_SELECT');
};


// Add this to App.tsx to improve the multiplayer game start logic
// Enhanced multiplayer ready change handler in App.tsx
const handleMultiplayerReadyChange = (isReady: boolean, isOpponent: boolean) => {
  debugLog(`Player ready change - isReady: ${isReady}, isOpponent: ${isOpponent}`);
  
  // Important: Update local state immediately
  if (isOpponent) {
    setMultiplayer(prev => ({
      ...prev,
      opponentReady: isReady
    }));
  } else {
    setMultiplayer(prev => ({
      ...prev,
      playerReady: isReady
    }));
    
    // Also notify the server about our own ready state
    const networkManager = NetworkManager.getInstance();
    networkManager.setReady(isReady);
  }
  
  debugLog(`Ready states AFTER update - Player: ${!isOpponent ? isReady : multiplayer.playerReady}, Opponent: ${isOpponent ? isReady : multiplayer.opponentReady}`);
};


// Add this handler function:
const handleControlPresetChange = (presetName: string, mapping: PresetMapping) => {
  setCurrentControlPreset(presetName);
  setControlMapping(mapping);
};

const handleSwitchToMultiplayer = () => {
  debugLog("Switching to multiplayer mode");
  setGameState('MULTIPLAYER');
};




useEffect(() => {
  // Check if user is coming from a portal
  const urlParams = new URLSearchParams(window.location.search);
  const isComingFromPortal = urlParams.get('portal') === 'true';
  const referrerUrl = urlParams.get('ref') || '';
  
  if (isComingFromPortal && referrerUrl) {
    debugLog("User arrived through portal from: ", referrerUrl);
    setIsFromPortal(true);
    setPortalRefUrl(referrerUrl);
  }
}, []);
// Handler function for animation toggle from MemeSelect
const handleAnimatedModelsToggle = (useAnimations: boolean) => {
  setUseAnimatedModels(useAnimations);
};

  // Toggle music state using SoundManager
  const toggleMusic = () => {
    const newMusicState = !musicOn;
    setMusicOn(newMusicState);
    
    if (newMusicState) {
      SoundManager.playSound('music', 0.5, true);
    } else {
      SoundManager.stopSound('music');
    }
  };

  // Handle computer opponent toggle
  const handleSetComputerOpponent = (isComputer: boolean) => {
    setIsComputerOpponent(isComputer);
  };

  const handleSwitchToAIMode = (useModernMode = true) => {
    setUseModernChallengeMode(useModernMode);
    setGameState('AI_MODE');
    
    // Track AI mode selection
    track('ModeSelected', { 
      mode: 'AI_Challenge', 
      variant: useModernMode ? 'Modern' : 'Classic'
    });
  };
  
  // Start the game from landing screen
  const handleStartGame = () => {
    setGameState('CHARACTER_SELECT');
    
    // Track game start
    track('GameStart', { initialScreen: 'CharacterSelect' });
  };
  
  // Switch to animation test screen
  const goToAnimationTest = () => {
    setGameState('ANIMATION_TEST');
  };

  // New function to go to meme test screen
  const goToMemeTest = () => {
    setGameState('MEME_TEST');
  };

  // Updated character selection handler with unselect support
  const handleCharacterSelect = (player: 1 | 2, character: Character) => {
    // Check if this is an unselect action (special ID for unselecting)
    if (character.id === 'unselect') {
      // Clear the appropriate player selection
      if (player === 1) {
        setPlayer1Character(null);
      } else {
        setPlayer2Character(null);
      }
  
      return;
    }
    
    // Handle normal selection
    if (player === 1) {
      setPlayer1Character(character);
    } else {
      setPlayer2Character(character);
    }

    // If both players have selected characters, move to interstitial
    if (!multiplayerRoomId && (player === 1 && player2Character) || (player === 2 && player1Character)) {
      setGameState('INTERSTITIAL');
    }
  };

  // Start the actual fight
  const startFight = () => {
    setGameState('PLAYING');
  };

  // Return to character select screen
  const returnToSelect = () => {
    setPlayer1Character(null);
    setPlayer2Character(null);
    setGameState('CHARACTER_SELECT');
  };

  // Return to landing screen
  const returnToLanding = () => {
    setGameState('LANDING');
  };

  // Render the current game state
  const renderGameState = () => {
    switch (gameState) {
      case 'LANDING':
        return (
          <LandingScreen 
          onStart={handleStartGame} 
          onMemeTest={goToMemeTest}
          externalMusicOn={musicOn}
          setExternalMusicOn={toggleMusic}
          isFromPortal={isFromPortal}
          portalRefUrl={portalRefUrl}
        />
        );
        
      case 'MEME_TEST':
        return (
          <Suspense fallback={lazyScreenFallback}>
            <TestMemeGenerator
              onReturnToMain={returnToLanding}
            />
          </Suspense>
        );
        
        case 'MULTIPLAYER':
          return (
            <Suspense fallback={lazyScreenFallback}>
              <MultiplayerLobby 
                onBack={returnToSelect}
                onStartGame={handleStartMultiplayerGame}
              />
            </Suspense>
          );
      case 'ANIMATION_TEST':
        return (
          <div style={{ width: '100%', height: '100vh' }}>
            <button 
              onClick={returnToSelect}
              style={{ 
                position: 'absolute', 
                top: '10px', 
                left: '10px', 
                zIndex: 1000,
                padding: '8px 16px',
                background: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Return to Character Select
            </button>
         
          </div>
        );
        
        case 'CHARACTER_SELECT':
          return (
            <Suspense fallback={lazyScreenFallback}>
              <div>
                <MemeSelect 
                  onSelect={handleCharacterSelect}
                  player1Selection={player1Character}
                  player2Selection={player2Character}
                  onSwitchToAIMode={handleSwitchToAIMode}
                  onSwitchToMultiplayer={handleSwitchToMultiplayer}
                  onSetComputerOpponent={handleSetComputerOpponent}
                  initialComputerOpponent={isComputerOpponent && !multiplayerRoomId} // Use computer opponent unless in multiplayer
                  externalMusicOn={musicOn}
                  onSetAnimatedModels={handleAnimatedModelsToggle}
                  initialAnimatedModels={useAnimatedModels}
                  currentControlPreset={currentControlPreset}
                  onControlPresetChange={handleControlPresetChange}
                  onMultiplayerReadyChange={handleMultiplayerReadyChange}
                  multiplayerRoomId={multiplayerRoomId} // Pass the room ID if in multiplayer mode
                  isMultiplayerHost={isMultiplayerHost} // Pass if this player is the host  
                />
              </div>
            </Suspense>
          );
      
    case 'INTERSTITIAL':
      return (
        <Suspense fallback={lazyScreenFallback}>
          <InterstitialScreen 
            player1={player1Character!}
            player2={player2Character!}
            onFightStart={startFight}
            onReturnToSelect={returnToSelect}
            useAnimatedModels={useAnimatedModels} // Pass the animation mode flag here
          />
        </Suspense>
      );
      
      case 'PLAYING':
        return (
          <Suspense fallback={lazyScreenFallback}>
            <GameScene 
              player1={player1Character!}
              player2={player2Character!}
              onReturnToSelect={returnToSelect}
              isComputerOpponent={isComputerOpponent}
              useAnimatedModels={useAnimatedModels}
              controlMapping={controlMapping}
              currentPreset={currentControlPreset}
              onPresetChange={handleControlPresetChange}
              isMultiplayerMode={Boolean(multiplayerRoomId)}
              player1Name={multiplayerPlayerNames.local}
              player2Name={multiplayerPlayerNames.remote}
            />
          </Suspense>
        );
      case 'AI_MODE':
        return (
          <Suspense fallback={lazyScreenFallback}>
            <AIGameMode onReturnToSelect={returnToSelect}
            useModernMode={useModernChallengeMode}  />
          </Suspense>
        
        );
      case 'TESTING':
        return (
          <div style={{ width: '100%', height: '100vh' }}>
            <button 
              onClick={returnToSelect}
              style={{ 
                position: 'absolute', 
                top: '10px', 
                left: '10px', 
                zIndex: 1000,
                padding: '8px 16px',
                background: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Return to Character Select
            </button>
          
          </div>
        );
      default:
        return <div>Unknown game state</div>;
    }
  };

  return (
    <>
      {/* Asset Preloader - runs in the background */}
      <AssetPreloader 
        onProgress={setPreloadProgress}
        onComplete={() => setInitialPreloadComplete(true)}
      />
  
      {/* Show global loading screen only on first load (optional) */}
      {!initialPreloadComplete && preloadProgress < 50 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#000033',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <h1>Dank Duel</h1>
          <div>Loading Game Assets: {preloadProgress}%</div>
          <div style={{
            width: '300px',
            height: '20px',
            backgroundColor: '#111',
            borderRadius: '10px',
            overflow: 'hidden',
            margin: '20px 0'
          }}>
            <div style={{
              width: `${preloadProgress}%`,
              height: '100%',
              backgroundColor: '#4e95d4',
              transition: 'width 0.3s ease-in-out'
            }}/>
          </div>
        </div>
      )}
      
      {/* Your existing renderGameState() output here */}
      {renderGameState()}
    </>
  );
};

export default App;
